import { useMemo } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useStore } from '../../store'

const DEFAULT_INDICES = ['SPY.US', 'QQQ.US', 'DIA.US', 'IWM.US']
const clean = (s: string) => s.replace(/\.[A-Z]+$/, '')

function buildSuggestions(watchlist: string[]): string[] {
  const own = watchlist.filter((s) => !DEFAULT_INDICES.includes(s)).map(clean)
  if (own.length >= 2) {
    return [
      `What moved $${own[0]} and $${own[1]} today, and why?`,
      `Show $${own[0]} on a 6-month daily chart with 50 & 200-day SMA`,
      `Compare $${own[0]} vs $${own[1]}: valuation, margins, growth`,
      `Catch me up on my whole watchlist`,
    ]
  }
  if (own.length === 1) {
    return [
      `What's the latest on $${own[0]}, and why?`,
      `Show $${own[0]} on a 6-month daily chart with 50 & 200-day SMA`,
      `Is $${own[0]} a buy here? Bull vs bear case`,
      `What moved the S&P 500 today, and why?`,
    ]
  }
  return [
    'What moved the S&P 500 today, and why?',
    'Show NVDA on a 6-month daily chart with 50 & 200-day SMA',
    'Compare AAPL vs MSFT: valuation, margins, growth',
    'If I invest $10k at 8%/yr, what is it worth in 15 years?',
  ]
}

export function EmptyState() {
  const send = useStore((s) => s.send)
  const health = useStore((s) => s.health)
  const user = useStore((s) => s.user)
  const watchlist = useStore((s) => s.watchlist)
  const suggestions = useMemo(() => buildSuggestions(watchlist), [watchlist])
  const firstName = user?.name?.trim().split(/\s+/)[0]
  const greeting = firstName ? `Good day, ${firstName}. I’m Libera.` : 'Good day. I’m Libera.'

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-xl text-center">
        <h1 className="text-[26px] font-semibold tracking-tight text-fg">{greeting}</h1>
        <p className="mx-auto mt-2 max-w-md text-[14px] text-fg-muted">
          Your live US-market research desk. Ask me anything — I read real-time data, search the
          web and X, draw charts, run exact calculations, and read images & files you attach.
        </p>

        {health && !health.xai && (
          <div className="mx-auto mt-5 flex max-w-md items-start gap-2 rounded-xl border border-[var(--line)] bg-[var(--down-wash)] px-3.5 py-2.5 text-left text-[12.5px] text-fg-muted">
            <AlertTriangle size={15} className="mt-0.5 shrink-0 text-down" />
            <span>
              No xAI key detected. Add <code>XAI_API_KEY</code> to <code>.env</code> and restart the
              server to wake me up. Live market data is already connected.
            </span>
          </div>
        )}

        <div className="mt-7 grid gap-2 sm:grid-cols-2">
          {suggestions.map((s) => (
            <button
              key={s}
              onClick={() => void send(s)}
              className="card px-3.5 py-3 text-left text-[13px] text-fg-muted transition-colors hover:border-[var(--line)] hover:text-fg"
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
